import React, {useContext, useEffect, useState, useRef} from "react";
import {useLazyQuery} from "@apollo/client";
import {DataGridPro, useGridApiRef} from '@mui/x-data-grid-pro';
import {Box, Stack, Tooltip, Button} from "@mui/material";
import moment from "moment-timezone";
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import {
    ALL_PUBLISHING_PLANS,
    ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION,
} from "../queries/GraphQLQueries";
import Loader from "../components/Loader";
import {TimezoneContext} from "../providers/TimezoneProvider";
import {buildPublishingPlanHierarchy} from "../helpers/ContentPublishingPlanHelper";
import OpenFile from "../components/OpenFile";
import {DATE_FORMAT_IN_COMPACT_READABLE_FORMAT} from "../config/DateTimeConstants";
import ContentHierarchyGroupingCell from "../components/common/ContentHierarchyGroupingCell";
import HierarchyActionButtons from "../components/common/HierarchyActionButtons";
import {shouldGroupExpand} from "../helpers/ContentHierarchyDataHelper";
import PublishingPlanCrudActionsDialog from "../components/publishing-plans/PublishingPlanCrudActionsDialog";
import CustomAlert from "../components/CustomAlert";

export default function PublishingPlansHierarchy() {

    const {timezone} = useContext(TimezoneContext);
    const dataGridApi = useGridApiRef();

    const [expandedState, setExpandedState] = useState(new Set());
    const [synthesizedColumns, setSynthesizedColumns] = useState([]);
    const [synthesizedRows, setSynthesizedRows] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [currentStartTime, setCurrentStartTime] = useState("");
    const [currentEndTime, setCurrentEndTime] = useState("");
    const [currentServiceType, setCurrentServiceType] = useState("");
    const [contentInfoName, setContentInfoName] = useState("");
    const [currentCapLevel, setCurrentCapLevel] = useState("");
    const [crudAction, setCrudAction] = useState("");
    const [planId, setPlanId] = useState(null);
    const [contentInfoId, setContentInfoId] = useState(null);
    const [originalPerpetualFlag, setOriginalPerpetualFlag] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({externalName: false, contentType: false, CAP_Level: false, contentFile: false});

    const detailPanelExpandedRowIds = useRef([]);
    const isUpdateComplete = useRef(false);

    const [fetchContentWithServiceTypeAndPublishing, {data: allPerformerContentWithServiceType, loading}] = useLazyQuery(ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION,
        {fetchPolicy: 'network-only'}
    );
    const [fetchAllPublishingPlan, {data: publishingPlanData}] = useLazyQuery(ALL_PUBLISHING_PLANS, {fetchPolicy: 'network-only'});

    const renderCellData = (value) => <Tooltip title={value}><Box>{value}</Box></Tooltip>;
    const renderFileCell = (params) => <OpenFile fileName={params.row.externalUrlName} fileUrl={params.row.externalUrl} />;
    const columnRenderer = params => <Tooltip title={params.colDef.headerName}><Box>{params.colDef.headerName}</Box></Tooltip>;
    const expandAll = () => {
        setExpandedState(new Set(synthesizedRows.map(row => row.id)));
        detailPanelExpandedRowIds.current = synthesizedRows.map(row => row.id);
    };
    
    const collapseAll = () => {
        setExpandedState(new Set());
        detailPanelExpandedRowIds.current = [];
    }

    const handleCloseDialog = () => {
        setDialogOpen(false);
        isUpdateComplete.current = true;
    }

    const renderDates = (date, isPerpetuallyPublished) => {
        let dateToShow = null;

        if (date && !isPerpetuallyPublished) {
            dateToShow = moment(date).format(DATE_FORMAT_IN_COMPACT_READABLE_FORMAT);
        }

        return renderCellData(dateToShow);
    }

    const renderPerpetualIcon = (params) => {

        const isPublishingPlanRow = !!params.row.start;
        const isPerpetuallyPublished = params.row.perpetual;

        if (isPublishingPlanRow && isPerpetuallyPublished) {
            return <CheckBoxIcon/>
        }

        return null;
    }

    const handleCloseCustomAlert = () => setAlertMessage(null);

    useEffect(() => {

        if (!allPerformerContentWithServiceType || !publishingPlanData || loading) {
            return;
        }

        const {
            publishingPlanRow,
            serviceTypeRow,
            sortedContentInfos,
            sortedContentFolders
        } = buildPublishingPlanHierarchy(publishingPlanData, allPerformerContentWithServiceType);

        setSynthesizedRows([...serviceTypeRow, ...sortedContentFolders, ...sortedContentInfos, ...publishingPlanRow]);
    }, [allPerformerContentWithServiceType, publishingPlanData, loading, timezone]);

    useEffect(() => {
        const commonColumnProperties = {
            width: 200,
            editable: false,
            sortable: false,
            headerAlign: 'start',
            align: 'start',
            renderHeader: columnRenderer
        }

        const handleCrudAction = (row, crudAction) => {
            
            const PLAN_ID_INDEX = 1;  
            const DECIMAL_RADIX = 10;
            isUpdateComplete.current = "in-progress";

            if (String(row.id).includes('_')) {
                setPlanId(parseInt(row.id.split('_')[PLAN_ID_INDEX], DECIMAL_RADIX));
            } else {
                setPlanId(row.id);
            }
            setContentInfoId(row.contentInfoId);
            setContentInfoName(row.displayName);
            setCurrentCapLevel(row.level);
            setCurrentStartTime(row.start);
            setCurrentEndTime(row.end);
            setCurrentServiceType(row.serviceTypeDescription);
            setOriginalPerpetualFlag(row.perpetual);
            setDialogOpen(true);
            setCrudAction(crudAction);
        }

        const columnHeaders = [
            {
                field: "externalName",
                headerName: "External Name",
                renderCell: (params) => renderCellData(params.row.externalName),
                ...commonColumnProperties
            },
            {
                field: "contentType",
                headerName: "Content Type",
                renderCell: (params) => renderCellData(params.row.contentType),
                ...commonColumnProperties
            },
            {
                field: "CAP_Level",
                headerName: "CAP Level",
                renderCell: (params) => renderCellData(params.row.level),
                ...commonColumnProperties
            },
            {
                field: "contentFile",
                headerName: "Content File",
                renderCell: (params) => renderFileCell(params),
                ...commonColumnProperties
            },
            {
                field: "Start",
                headerName: "Start",
                renderCell: (params) => renderDates(params.row.start, params.row.perpetual),
                ...commonColumnProperties
            },
            {
                field: "End",
                headerName: "End",
                renderCell: (params) => renderDates(params.row.end, params.row.perpetual),
                ...commonColumnProperties
            },
            {
                field: "Perpetual",
                headerName: "Perpetual",
                renderCell: (params) => renderPerpetualIcon(params),
                ...commonColumnProperties
            },
            {
                field: "Actions",
                headerName: "Actions",
                renderCell: (params) => {

                    const isContentInfoRow = !!params.row.externalUrl;
                    const isPublishingPlanRow = !!params.row.start;

                    return (
                      <Stack direction="row" spacing={1}>
                          {isContentInfoRow && <Button variant="contained" onClick={() => handleCrudAction(params.row, 'Create')}>Create</Button>}
                          {isPublishingPlanRow
                            && <Stack direction="row" spacing={1}>
                                <Box marginLeft="80px"></Box>
                                <Button variant="contained" onClick={() => handleCrudAction(params.row, 'Edit')}>Edit</Button>
                                <Button variant="contained" onClick={() => handleCrudAction(params.row, 'Delete')}>Delete</Button>
                            </Stack>}
                      </Stack>
                    );
                },
                ...commonColumnProperties,
                width: 270
            }
        ];

        setSynthesizedColumns(columnHeaders);
    }, [synthesizedRows]);

    useEffect(() => refreshData(), []);

    const refreshData = () => {
        fetchContentWithServiceTypeAndPublishing();
        fetchAllPublishingPlan();
    }

    if (loading) {
        return <Loader/>;
    }

    const planHierarchyGroupColumnDefinition = {
        headerName: "Plan Hierarchy",
        width: 300,
        renderCell: params => <ContentHierarchyGroupingCell
                                  detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                                  isUpdateComplete={isUpdateComplete}
                                  {...params} />
    }

    // This style freezes the top bar along with hierarchy header row when user scrolls down
    return <Stack style={{height: 'calc(100vh - 16px)'}}>
        <HierarchyActionButtons
          onRefresh={refreshData}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll} />
        <DataGridPro
          treeData
          sx={{"& .MuiDataGrid-cell": {display: "flex", alignItems: "center"}}}
          hideFooter={true}
          disableMultipleColumnsSorting={true}
          apiRef={dataGridApi}
          isGroupExpandedByDefault={params => shouldGroupExpand(params, new Set([...detailPanelExpandedRowIds.current]))}
          rows={synthesizedRows}
          columns={synthesizedColumns}
          columnVisibilityModel={visibleColumns}
          onColumnVisibilityModelChange={newModel => setVisibleColumns(newModel)}
          getTreeDataPath={row => row.hierarchy}
          groupingColDef={planHierarchyGroupColumnDefinition} />

        <PublishingPlanCrudActionsDialog 
            currentStartTime={currentStartTime}
            currentEndTime={currentEndTime}
            currentServiceType={currentServiceType}
            contentInfoName={contentInfoName}
            currentCapLevel={currentCapLevel}
            crudAction={crudAction}
            planId={planId}
            contentInfoId={contentInfoId}
            originalPerpetualFlag={originalPerpetualFlag}
            openPublishingPlanCrudActionsDialog={dialogOpen}
            handleClose={handleCloseDialog} />

        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </Stack>;
}
